/**
 * Settings Page
 *
 * Centralized settings management with 6 tabs:
 * - Profile: Personal information
 * - Business: Company/employer details
 * - Subscription: Plan and usage
 * - Billing: Payment history
 * - API Keys: API key management
 * - Security: Password and security settings
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  DocumentTextIcon,
  KeyIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { ProfileSettings } from '@components/settings/ProfileSettings';
import { BusinessSettings } from '@components/settings/BusinessSettings';
import { SubscriptionSettings } from '@components/settings/SubscriptionSettings';
import { BillingSettings } from '@components/settings/BillingSettings';
import { ApiKeySettings } from '@components/settings/ApiKeySettings';
import { SecuritySettings } from '@components/settings/SecuritySettings';

type Tab =
  | 'profile'
  | 'business'
  | 'subscription'
  | 'billing'
  | 'api-keys'
  | 'security';

const tabs: { id: Tab; name: string; icon: typeof UserCircleIcon }[] = [
  { id: 'profile', name: 'Profile', icon: UserCircleIcon },
  { id: 'business', name: 'Business', icon: BuildingOfficeIcon },
  { id: 'subscription', name: 'Subscription', icon: CreditCardIcon },
  { id: 'billing', name: 'Billing', icon: DocumentTextIcon },
  { id: 'api-keys', name: 'API Keys', icon: KeyIcon },
  { id: 'security', name: 'Security', icon: ShieldCheckIcon },
];

const validTabs = new Set<string>(tabs.map(t => t.id));

export const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = validTabs.has(tabParam || '')
    ? (tabParam as Tab)
    : 'profile';

  // Update URL when tab changes
  const handleTabChange = (tabId: Tab) => {
    setSearchParams({ tab: tabId });
  };

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'business':
        return <BusinessSettings />;
      case 'subscription':
        return <SubscriptionSettings />;
      case 'billing':
        return <BillingSettings />;
      case 'api-keys':
        return <ApiKeySettings />;
      case 'security':
        return <SecuritySettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-gray-600">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${
                    isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon
                  className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${
                      isActive
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }
                  `}
                />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>{renderTabContent()}</div>
    </div>
  );
};

export default Settings;
